
import React from 'react';
import { ComboBox } from 'office-ui-fabric-react/lib/ComboBox';
import { PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import { mergeStyleSets } from 'office-ui-fabric-react/lib/Styling';
import { useSelector  } from 'react-redux';
import WKT from 'ol/format/WKT';

import {
    Log,
    PanelSectionTitle
} from '../../core';

import { getTempFeature } from '../../map';

import { getUi } from '../reducers'
import { getQuery } from '../reducers/definition'

import {
    getAttrConditions,
    getIsGeoActive,
    getIsValid,
    getParamValues,
    getQueryId
} from '../reducers/ui'

import SearchFormAttributes from './SearchFormAttributes';
import SearchFormParams from './SearchFormParams';

const log = Log.getLogger('MultiSceneForApp.search.SearchForm');

const SearchForm = ({
    definition,
    onDoSearch,
    onSelectQuery,
    onSetAttrCondition,
    onSetGeomCondition,
    onSetGeometry,
    onSetIsGeoActive,
    onSetIsValid,
    onSetParamValue
}) => {

    const classNames = mergeStyleSets({
        SearchForm: {
            height: '100%',
            minHeight: 0,
            overflow: 'hidden'
        },
        queries: {
            flexShrink: 0,
            paddingBottom: 8
        },
        content: {
            minHeight: 0,
            overflowY: 'auto',
            paddingRight: 4
        },
        actions: {
            flexShrink: 0
        }
    });

    const attrConditions = useSelector(state => getAttrConditions(getUi(state.search)));
    const isGeoActive = useSelector(state => getIsGeoActive(getUi(state.search)));
    const isValid = useSelector(state => getIsValid(getUi(state.search)));
    const paramValues = useSelector(state => getParamValues(getUi(state.search)));
    const queryId = useSelector(state => getQueryId(getUi(state.search)));

    const partialSearchResult = useSelector(state => (state.search.search.searchResult ? !!state.search.search.searchResult.totalCount : false));

    const tempFeature = useSelector(state => getTempFeature(state.map.map));

    React.useEffect(() => {
        if (onSetGeomCondition) {
            let format = new WKT();
            let geom = tempFeature ? format.writeGeometry(tempFeature.getGeometry()) : null;
            onSetGeomCondition(
                geom, 'st_intersects'
            );
        }
    }, [tempFeature]);

    const doSearch = (operation) => {
        if (onDoSearch) {
            onDoSearch(queryId, operation);
        }
    }

    const getQueriesOptions = () => {
        return definition.searchDefinition
            .filter(item => (item.hidden !== true))
            .map(item => {
                return {
                    key: item.id,
                    text: item.title,
                    queryType: item.type
                };
            });
    }

    const getDefinitionForGeneratePolygon = () => {
        return definition.searchDefinition.reduce((previous, current) => {
            return (!previous && (current.hidden && current.subType === 'GeneratePolygon'))
                ? current
                : previous;
        }, null);
    }

    const handleClickSearch = (evt) => {
        doSearch('SET');
    }

    const handleChangeQuery = (evt, option, index, value) => {
        if (onSelectQuery) {
            onSelectQuery(option.key, option.queryType);
        }
        handleSetIsValid(false);
    }

    const handleChangeValid = (valid) => {
        handleSetIsValid(valid);
    }

    const handleDoSearch = () => {
        doSearch('SET');
    }

    const handleSetGeometry = (geometry, zoomToGeometry) => {
        if (onSetGeometry) {
            onSetGeometry(geometry, zoomToGeometry);
        }
    }

    const handleSetIsValid = (valid) => {
        if (onSetIsValid) {
            onSetIsValid(valid);
        }
    }

    const renderSearchFormContent = () => {
        let query = getQuery(definition, queryId);
        const queryType = query.type || 'PARAMS';
        switch (queryType) {
            case 'ATTRIBUTES':
                return (
                    <SearchFormAttributes
                        attrConditions={attrConditions}
                        generatePolygonDefinition={getDefinitionForGeneratePolygon()}
                        isGeoActive={isGeoActive}
                        searchDefinition={query}
                        tempFeature={tempFeature}
                        onSetGeometry={handleSetGeometry}
                        onSetIsGeoActive={onSetIsGeoActive}
                        onSetAttrCondition={onSetAttrCondition}
                    />
                );
            case 'PARAMS':
                return (
                    <SearchFormParams
                        generatePolygonDefinition={getDefinitionForGeneratePolygon()}
                        paramValues={paramValues}
                        searchDefinition={query}
                        tempFeature={tempFeature}
                        onDoSearch={handleDoSearch}
                        onChangeValid={handleChangeValid}
                        onSetGeometry={handleSetGeometry}
                        onSetIsGeoActive={onSetIsGeoActive}
                        onSetParamValue={onSetParamValue}
                    />
                );
            default:
                log.warn(`Unknown query type "${queryType}"`);
        }

    }

    return (
        <Stack className={classNames.SearchForm} tokens={{childrenGap: 4}} >
            <Stack.Item align="stretch" className={classNames.queries}>
                <PanelSectionTitle title="Co hledáte" />
                <ComboBox
                    selectedKey={queryId}
                    options={getQueriesOptions()}
                    onChange={handleChangeQuery}
                />
            </Stack.Item>

            <Stack.Item grow={1} shrink={1} className={classNames.content}>
                {renderSearchFormContent()}
            </Stack.Item>

            <Stack.Item align="end" className={classNames.actions}>
                <PrimaryButton
                    onClick={handleClickSearch}
                    text="Vyhledat"
                    disabled={!isValid}
                    split
                    menuProps={{
                        items: [{
                            key: 'ADD',
                            text: 'Vyhledat a přidat',
                            disabled: partialSearchResult,
                            onClick: () => doSearch('ADD')
                        }, {
                            key: 'REMOVE',
                            text: 'Vyhledat a odebrat',
                            disabled: partialSearchResult,
                            onClick: () => doSearch('REMOVE')
                        }, {
                            key: 'FILTER',
                            text: 'Vyhledat a filtrovat',
                            disabled: partialSearchResult,
                            onClick: () => doSearch('FILTER')
                        }]
                    }}
                />
            </Stack.Item>
        </Stack>
    );
}

export default SearchForm;
